import { useEffect, useState, useMemo, useRef } from "react";
import { Mail, Send, FileText, History, Pencil, Trash2, Search, Plus, X, Paperclip, RefreshCw, Eye, AlertCircle, CheckCircle2, AlertTriangle, Save, Users, Copy, Sparkles, Languages, Wand2, ClipboardCheck, MessageSquare, Clock, CalendarClock, MailCheck, MailX, MailOpen } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useCompany, fmtNipc } from "../../context/CompanyContext";

/**
 * AdminEmailsStudio — phase 1 of the professional email module.
 *
 * Three tabs:
 *   1. Compose     — write & send a new email (with template picker,
 *                    recipient autocomplete from contacts book, attachments)
 *   2. History     — browse sent emails with filters; resend / view body
 *   3. Templates   — manage the 18 seeded + custom templates
 *
 * Drafts: composer offers «Guardar Rascunho» — drafts appear inline above
 * the form. Click to resume editing.
 *
 * Phase 2 will add: AI assist, multi-language, scheduling, campaigns.
 */
export default function AdminEmailsStudio() {
  const [tab, setTab] = useState("compose");

  return (
    <div className="space-y-6" data-testid="admin-emails-studio">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
            <Mail size={22} className="text-amber-300" /> Comunicações
          </h1>
          <p className="text-sm text-emerald-200/70 mt-1">
            Envio profissional de e-mails para clientes, proprietários e fornecedores. Inclui histórico, modelos e anexos.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-emerald-300/15 overflow-x-auto">
        {[
          { key: "compose", label: "Novo E-mail", icon: Send },
          { key: "history", label: "Histórico", icon: History },
          { key: "templates", label: "Modelos", icon: FileText },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2 border-b-2 -mb-px transition whitespace-nowrap ${
                    tab === t.key
                      ? "text-amber-300 border-amber-300"
                      : "text-emerald-200/70 border-transparent hover:text-emerald-100"
                  }`}
                  data-testid={`ae-tab-${t.key}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "compose" && <ComposeTab />}
      {tab === "history" && <HistoryTab onJump={(seed) => setTab("compose") || window.dispatchEvent(new CustomEvent("ae-compose-seed", { detail: seed }))} />}
      {tab === "templates" && <TemplatesTab />}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── COMPOSE */

function ComposeTab() {
  const [to, setTo] = useState([]);
  const [toInput, setToInput] = useState("");
  const [cc, setCc] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [bcc, setBcc] = useState([]);
  const [bccInput, setBccInput] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [contactQuery, setContactQuery] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [scheduledFor, setScheduledFor] = useState("");
  const [placeholders, setPlaceholders] = useState({});
  const fileInputRef = useRef(null);

  // Detect __KEY__ tokens in subject + body, surface a fillable input for each
  const detectedPlaceholders = useMemo(() => {
    const text = `${subject}\n${body}`;
    const matches = text.match(/__[A-Z][A-Z0-9_]*__/g) || [];
    const uniq = Array.from(new Set(matches.map((m) => m.replace(/^__|__$/g, ""))));
    return uniq;
  }, [subject, body]);

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    const handler = (e) => seedFromHistory(e.detail);
    window.addEventListener("ae-compose-seed", handler);
    return () => window.removeEventListener("ae-compose-seed", handler);
  }, []);

  async function loadAll() {
    try {
      const [{ data: c }, { data: t }, { data: d }] = await Promise.all([
        api.get("/admin/emails/contacts"),
        api.get("/admin/emails/templates"),
        api.get("/admin/emails/drafts"),
      ]);
      setContacts(c.items || []);
      setTemplates(t.items || []);
      setDrafts(d.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function seedFromHistory(m) {
    if (!m) return;
    setTo(m.to || []);
    setCc(m.cc || []);
    setBcc(m.bcc || []);
    if ((m.cc?.length || 0) + (m.bcc?.length || 0) > 0) setShowCcBcc(true);
    setSubject(m.subject?.replace(/^\[Reenvio\]\s*/, "") || "");
    setBody(m.body_html || "");
    setTemplateId(m.template_id || "");
    toast.success("Conteúdo carregado para reenvio.");
  }

  function applyTemplate(id) {
    setTemplateId(id);
    if (!id) return;
    const t = templates.find((x) => x.id === id);
    if (t) {
      setSubject(t.subject);
      setBody(t.body_html);
    }
  }

  function addRecipient(field, value) {
    const v = (value || "").trim();
    if (!v) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
      toast.error(`Email inválido: ${v}`);
      return;
    }
    if (field === "to") {
      if (!to.includes(v)) setTo([...to, v]);
      setToInput("");
    } else if (field === "cc") {
      if (!cc.includes(v)) setCc([...cc, v]);
      setCcInput("");
    } else {
      if (!bcc.includes(v)) setBcc([...bcc, v]);
      setBccInput("");
    }
  }

  function removeRecipient(field, value) {
    if (field === "to") setTo(to.filter((x) => x !== value));
    else if (field === "cc") setCc(cc.filter((x) => x !== value));
    else setBcc(bcc.filter((x) => x !== value));
  }

  function pickContact(c) {
    if (!to.includes(c.email)) setTo([...to, c.email]);
    setContactsOpen(false);
    setContactQuery("");
  }

  const filteredContacts = useMemo(() => {
    const q = contactQuery.trim().toLowerCase();
    if (!q) return contacts.slice(0, 50);
    return contacts.filter((c) =>
      (c.name || "").toLowerCase().includes(q)
      || (c.email || "").toLowerCase().includes(q)
      || (c.type || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [contacts, contactQuery]);

  async function handleFiles(files) {
    const arr = Array.from(files);
    const newOnes = [];
    for (const f of arr) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`«${f.name}» excede 10MB.`);
        continue;
      }
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(f);
      });
      newOnes.push({
        filename: f.name,
        content_base64: b64,
        content_type: f.type || "application/octet-stream",
        size: f.size,
      });
    }
    setAttachments([...attachments, ...newOnes]);
  }

  function removeAttachment(idx) {
    setAttachments(attachments.filter((_, i) => i !== idx));
  }

  function buildPayload() {
    return {
      to, cc, bcc,
      subject: subject.trim(),
      body_html: body.trim(),
      template_id: templateId || null,
      attachments,
      scheduled_for: scheduledFor || null,
      placeholders,
    };
  }

  async function send() {
    if (to.length === 0) { toast.error("Indique pelo menos um destinatário."); return; }
    if (!subject.trim()) { toast.error("Assunto obrigatório."); return; }
    if (!body.trim()) { toast.error("Mensagem obrigatória."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/emails/send", buildPayload());
      if (data.status === "scheduled") {
        toast.success(`E-mail agendado para ${new Date(data.scheduled_for).toLocaleString("pt-PT")}.`);
      } else {
        toast.success(
          data.status === "sent"
            ? `E-mail enviado para ${data.sent_to.length} destinatário(s).`
            : `Envio parcial: ${data.sent_to.length} ok, ${data.failed.length} falhou.`,
        );
      }
      // Clean up + drop draft if was editing one
      if (draftId) {
        try { await api.delete(`/admin/emails/drafts/${draftId}`); } catch {} // noqa
      }
      setTo([]); setCc([]); setBcc([]);
      setSubject(""); setBody("");
      setTemplateId(""); setAttachments([]);
      setScheduledFor("");
      setPlaceholders({});
      setDraftId(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  }

  /* AI helpers — never throws (toast on error), returns body+optional subject */
  async function aiCall(endpoint, payload, opts = {}) {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/admin/emails/ai/${endpoint}`, payload);
      if (data.subject && opts.applySubject) setSubject(data.subject);
      if (data.body_html) setBody(data.body_html);
      toast.success(opts.successMsg || "IA aplicou as alterações.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiBusy(false);
      setAiOpen(false);
    }
  }

  async function aiGenerate(prompt, tone) {
    if (!prompt?.trim()) { toast.error("Descreva o que pretende."); return; }
    await aiCall("generate", { prompt, tone, language: "pt" }, {
      applySubject: true,
      successMsg: "Esboço gerado pela IA.",
    });
  }
  async function aiImprove() {
    if (!body.trim()) { toast.error("Escreva ou cole texto primeiro."); return; }
    await aiCall("improve", { text: body, language: "pt" }, { successMsg: "Texto melhorado." });
  }
  async function aiGrammar() {
    if (!body.trim()) { toast.error("Escreva ou cole texto primeiro."); return; }
    await aiCall("grammar", { text: body, language: "pt" }, { successMsg: "Gramática corrigida." });
  }
  async function aiTranslate(target) {
    if (!body.trim()) { toast.error("Escreva ou cole texto primeiro."); return; }
    await aiCall("translate", { text: body, target_language: target }, { successMsg: `Traduzido para ${target.toUpperCase()}.` });
  }
  async function aiAdaptTone(tone) {
    if (!body.trim()) { toast.error("Escreva ou cole texto primeiro."); return; }
    await aiCall("adapt-tone", { text: body, tone, language: "pt" }, { successMsg: `Tom adaptado: ${tone}.` });
  }

  async function saveDraft() {
    setSavingDraft(true);
    try {
      const payload = { to, cc, bcc, subject, body_html: body, template_id: templateId || null };
      if (draftId) {
        await api.patch(`/admin/emails/drafts/${draftId}`, payload);
        toast.success("Rascunho atualizado.");
      } else {
        const { data } = await api.post("/admin/emails/drafts", payload);
        setDraftId(data.id);
        toast.success("Rascunho guardado.");
      }
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingDraft(false);
    }
  }

  function loadDraft(d) {
    setDraftId(d.id);
    setTo(d.to || []);
    setCc(d.cc || []);
    setBcc(d.bcc || []);
    if ((d.cc?.length || 0) + (d.bcc?.length || 0) > 0) setShowCcBcc(true);
    setSubject(d.subject || "");
    setBody(d.body_html || "");
    setTemplateId(d.template_id || "");
    toast.success("Rascunho carregado.");
  }

  async function deleteDraft(id) {
    if (!window.confirm("Eliminar este rascunho?")) return;
    try {
      await api.delete(`/admin/emails/drafts/${id}`);
      if (draftId === id) setDraftId(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="space-y-4">
      {/* Drafts inline */}
      {drafts.length > 0 && (
        <div className="rounded-xl border border-amber-300/25 bg-amber-300/5 p-3" data-testid="ae-drafts-list">
          <p className="text-[11px] text-amber-200/70 uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
            <Save size={12} /> Rascunhos ({drafts.length})
          </p>
          <div className="flex gap-2 flex-wrap">
            {drafts.map((d) => (
              <div key={d.id}
                   className={`rounded-lg border px-3 py-2 text-xs flex items-center gap-2 transition ${
                     d.id === draftId
                       ? "border-amber-300/60 bg-amber-300/10 text-amber-100"
                       : "border-emerald-300/20 bg-emerald-900/40 text-emerald-100 hover:bg-emerald-300/10"
                   }`}>
                <button onClick={() => loadDraft(d)} className="text-left truncate max-w-[200px]"
                        data-testid={`ae-draft-${d.id}`}>
                  {d.subject || "(sem assunto)"}
                </button>
                <button onClick={() => deleteDraft(d.id)} className="text-red-300/70 hover:text-red-200">
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form */}
      <div className="rounded-2xl border border-emerald-300/15 bg-emerald-950/50 p-5 space-y-4">
        {/* Template + contacts */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label icon={<FileText size={11} />}>Modelo (opcional)</Label>
            <select value={templateId}
                    onChange={(e) => applyTemplate(e.target.value)}
                    className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-amber-300/50"
                    data-testid="ae-template-select">
              <option value="">— Sem modelo —</option>
              {Object.entries(groupBy(templates, "category")).map(([cat, arr]) => (
                <optgroup key={cat} label={cat}>
                  {arr.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div>
            <Label icon={<Users size={11} />}>Contactos guardados ({contacts.length})</Label>
            <button onClick={() => setContactsOpen(!contactsOpen)}
                    className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-emerald-100 text-sm px-3 py-2 hover:bg-emerald-900/70 inline-flex items-center gap-2 justify-between"
                    data-testid="ae-contacts-toggle">
              <span>{contactsOpen ? "Fechar lista" : "Abrir lista de contactos"}</span>
              <Search size={12} />
            </button>
          </div>
        </div>

        {contactsOpen && (
          <div className="rounded-lg border border-emerald-300/20 bg-emerald-900/30 p-3 space-y-2" data-testid="ae-contacts-panel">
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-200/40" />
              <input type="text" value={contactQuery}
                     onChange={(e) => setContactQuery(e.target.value)}
                     placeholder="Pesquisar contacto…"
                     className="w-full rounded-lg bg-emerald-900/60 border border-emerald-300/20 text-white text-sm pl-8 pr-3 py-1.5 focus:outline-none focus:border-amber-300/50"
                     data-testid="ae-contacts-search" autoFocus />
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-emerald-300/10">
              {filteredContacts.length === 0 ? (
                <p className="p-2 text-xs text-emerald-200/60 text-center">Sem resultados.</p>
              ) : filteredContacts.map((c, i) => (
                <button key={`${c.email}-${i}`}
                        onClick={() => pickContact(c)}
                        className="w-full text-left px-2 py-1.5 hover:bg-emerald-300/10 rounded text-sm flex items-center justify-between"
                        data-testid={`ae-contact-pick-${i}`}>
                  <div className="min-w-0 flex-1">
                    <span className="text-white font-medium truncate block">{c.name}</span>
                    <span className="text-[11px] text-emerald-200/50 truncate block">{c.email}</span>
                  </div>
                  <span className="text-[10px] rounded-full bg-emerald-400/15 text-emerald-200 px-2 py-0.5 uppercase tracking-wider font-semibold shrink-0 ml-2">{c.type}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Recipients */}
        <RecipientField label="Para" tags={to} input={toInput} setInput={setToInput}
                        onAdd={(v) => addRecipient("to", v)} onRemove={(v) => removeRecipient("to", v)}
                        testid="ae-to" required />
        {showCcBcc ? (
          <>
            <RecipientField label="CC" tags={cc} input={ccInput} setInput={setCcInput}
                            onAdd={(v) => addRecipient("cc", v)} onRemove={(v) => removeRecipient("cc", v)}
                            testid="ae-cc" />
            <RecipientField label="BCC" tags={bcc} input={bccInput} setInput={setBccInput}
                            onAdd={(v) => addRecipient("bcc", v)} onRemove={(v) => removeRecipient("bcc", v)}
                            testid="ae-bcc" />
          </>
        ) : (
          <button onClick={() => setShowCcBcc(true)}
                  className="text-xs text-emerald-200/70 hover:text-emerald-100 underline underline-offset-2"
                  data-testid="ae-show-cc-bcc">
            + Adicionar CC / BCC
          </button>
        )}

        {/* Subject + body */}
        <div>
          <Label>Assunto *</Label>
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                 placeholder="Assunto do e-mail"
                 className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-amber-300/50"
                 data-testid="ae-subject" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
            <Label>Mensagem *</Label>
            <button onClick={() => setAiOpen(!aiOpen)}
                    type="button"
                    disabled={aiBusy}
                    className="text-xs rounded-lg border border-amber-300/40 bg-amber-300/10 text-amber-200 hover:bg-amber-300/20 px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
                    data-testid="ae-ai-toggle">
              <Sparkles size={12} /> {aiOpen ? "Fechar assistente" : "Assistente IA"}
            </button>
          </div>
          {aiOpen && (
            <AiPanel busy={aiBusy}
                     onGenerate={aiGenerate}
                     onImprove={aiImprove}
                     onGrammar={aiGrammar}
                     onTranslate={aiTranslate}
                     onAdaptTone={aiAdaptTone} />
          )}
          <textarea value={body} onChange={(e) => setBody(e.target.value)}
                    rows={12} placeholder="Escreva a mensagem. HTML básico permitido (p, strong, em, ul, li, br, a)…"
                    className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm px-3 py-2 font-mono focus:outline-none focus:border-amber-300/50"
                    data-testid="ae-body" />
          <p className="text-[11px] text-emerald-200/50 mt-1">
            O cabeçalho com logotipo e o rodapé com assinatura serão adicionados automaticamente.
          </p>
        </div>

        {/* Attachments */}
        <div>
          <Label icon={<Paperclip size={11} />}>Anexos (máx 10MB cada · 20MB total)</Label>
          <input ref={fileInputRef} type="file" multiple hidden onChange={(e) => handleFiles(e.target.files)}
                 accept=".pdf,.docx,.xlsx,.doc,.xls,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.zip"
                 data-testid="ae-file-input" />
          <button onClick={() => fileInputRef.current?.click()}
                  className="rounded-lg border border-emerald-300/30 bg-emerald-300/10 text-emerald-100 hover:bg-emerald-300/20 text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
                  data-testid="ae-attach-btn">
            <Paperclip size={12} /> Anexar ficheiro
          </button>
          {attachments.length > 0 && (
            <div className="mt-2 space-y-1">
              {attachments.map((a, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-emerald-900/40 border border-emerald-300/10 px-3 py-1.5 text-xs">
                  <span className="text-emerald-100 truncate flex items-center gap-2">
                    <Paperclip size={11} /> {a.filename} <span className="text-emerald-200/50">({fmtBytes(a.size)})</span>
                  </span>
                  <button onClick={() => removeAttachment(i)} className="text-red-300 hover:text-red-200">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Placeholder filler — auto-shows when body/subject contains __KEY__ tokens */}
      {detectedPlaceholders.length > 0 && (
        <div className="rounded-xl border border-amber-300/40 bg-amber-300/10 p-3" data-testid="ae-placeholders-section">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
            <p className="text-[11px] uppercase tracking-wider text-amber-200 font-semibold inline-flex items-center gap-1.5">
              <FileText size={11} /> Preencher campos do modelo ({detectedPlaceholders.length})
            </p>
            <p className="text-[10px] text-emerald-200/60">
              Estes valores substituem os marcadores no e-mail final.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {detectedPlaceholders.map((key) => (
              <div key={key} className="flex items-center gap-2">
                <label className="text-[11px] font-mono text-amber-300 whitespace-nowrap min-w-[80px]">
                  __{key}__
                </label>
                <input type="text"
                       value={placeholders[key] || ""}
                       onChange={(e) => setPlaceholders({ ...placeholders, [key]: e.target.value })}
                       placeholder={key === "VALOR" ? "ex: 75.00" : key === "DATA" ? "ex: 15/06/2026" : ""}
                       className="flex-1 rounded-lg bg-emerald-900/60 border border-emerald-300/20 text-white text-sm px-2 py-1 focus:outline-none focus:border-amber-300/50"
                       data-testid={`ae-placeholder-${key}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Schedule (optional) */}
      <div className="rounded-xl border border-emerald-300/15 bg-emerald-900/30 p-3" data-testid="ae-schedule-section">
        <label className="text-[11px] uppercase tracking-wider text-emerald-200/60 font-semibold flex items-center gap-1.5 mb-2">
          <CalendarClock size={11} className="text-amber-300" />
          Agendar envio (opcional)
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="datetime-local"
                 value={scheduledFor ? new Date(scheduledFor).toISOString().slice(0, 16) : ""}
                 min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                 onChange={(e) => setScheduledFor(e.target.value ? new Date(e.target.value).toISOString() : "")}
                 className="rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm px-3 py-1.5 focus:outline-none focus:border-amber-300/50"
                 data-testid="ae-schedule-input" />
          {scheduledFor && (
            <button onClick={() => setScheduledFor("")} type="button"
                    className="text-xs text-emerald-200/70 hover:text-white inline-flex items-center gap-1"
                    data-testid="ae-schedule-clear">
              <X size={12} /> Limpar
            </button>
          )}
          {scheduledFor && (
            <span className="text-xs text-amber-300 inline-flex items-center gap-1">
              <Clock size={11} /> Será enviado em {new Date(scheduledFor).toLocaleString("pt-PT")}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
        <button onClick={() => setShowPreview(true)} disabled={!body.trim()}
                className="rounded-lg border border-emerald-300/30 bg-emerald-300/5 px-5 py-2.5 text-sm text-emerald-100 hover:bg-emerald-300/10 disabled:opacity-50 inline-flex items-center gap-2 justify-center"
                data-testid="ae-preview-btn">
          <Eye size={14} /> Pré-visualizar
        </button>
        <button onClick={saveDraft} disabled={savingDraft || sending}
                className="rounded-lg border border-emerald-300/40 bg-emerald-300/10 px-5 py-2.5 text-sm font-semibold text-emerald-100 hover:bg-emerald-300/20 disabled:opacity-50 inline-flex items-center gap-2 justify-center"
                data-testid="ae-save-draft">
          {savingDraft ? "A guardar…" : <><Save size={14} /> Guardar Rascunho</>}
        </button>
        <button onClick={send} disabled={sending || to.length === 0}
                className="rounded-lg bg-amber-300 hover:bg-amber-200 text-emerald-950 px-5 py-2.5 text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2 justify-center"
                data-testid="ae-send-btn">
          {sending
            ? (scheduledFor ? "A agendar…" : "A enviar…")
            : (scheduledFor
                ? <><CalendarClock size={14} /> Agendar Envio</>
                : <><Send size={14} /> Enviar E-mail</>)}
        </button>
      </div>

      {showPreview && (
        <PreviewModal subject={subject}
                      body={body}
                      placeholders={placeholders}
                      onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── HISTORY */

function HistoryTab({ onJump }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [detail, setDetail] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (status) params.status = status;
      // Pool history + scheduled (only when status filter is empty or 'scheduled')
      const promises = [api.get("/admin/emails/history", { params })];
      if (!status || status === "scheduled") {
        promises.push(api.get("/admin/emails/scheduled"));
      }
      const results = await Promise.all(promises);
      const sent = results[0].data.items || [];
      const sched = results[1] ? results[1].data.items || [] : [];
      // Annotate scheduled rows so their sort-key uses scheduled_for and
      // they can be filtered separately in the UI.
      const merged = [...sched, ...sent].sort((a, b) => {
        const ta = a.scheduled_for || a.sent_at || a.created_at || "";
        const tb = b.scheduled_for || b.sent_at || b.created_at || "";
        return tb.localeCompare(ta);
      });
      setItems(merged);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  async function cancelScheduled(id) {
    if (!window.confirm("Cancelar este envio agendado?")) return;
    try {
      await api.delete(`/admin/emails/scheduled/${id}`);
      toast.success("Envio agendado cancelado.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line

  async function resend(id) {
    if (!window.confirm("Reenviar este e-mail aos mesmos destinatários?")) return;
    try {
      await api.post(`/admin/emails/history/${id}/resend`);
      toast.success("E-mail reenviado.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function viewDetail(id) {
    try {
      const { data } = await api.get(`/admin/emails/history/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald-200/40" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 placeholder="Pesquisar por assunto, destinatário ou utilizador…"
                 className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm pl-8 pr-3 py-2 focus:outline-none focus:border-amber-300/50"
                 data-testid="ae-history-search" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm px-3 py-2"
                data-testid="ae-history-status">
          <option value="">Todos os estados</option>
          <option value="sent">Enviado</option>
          <option value="partial">Parcial</option>
          <option value="failed">Falhou</option>
          <option value="scheduled">Agendado</option>
        </select>
        <button onClick={load}
                className="rounded-lg border border-emerald-300/30 bg-emerald-300/10 text-emerald-100 hover:bg-emerald-300/20 text-sm px-4 py-2 inline-flex items-center gap-2"
                data-testid="ae-history-refresh">
          <RefreshCw size={12} /> Filtrar
        </button>
      </div>

      {/* List */}
      <div className="rounded-2xl border border-emerald-300/15 bg-emerald-950/40 overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-emerald-200/60 text-sm">A carregar…</p>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-emerald-200/60 text-sm" data-testid="ae-history-empty">
            Ainda não há e-mails enviados.
          </p>
        ) : (
          <div className="divide-y divide-emerald-300/10">
            {items.map((m) => (
              <div key={m.id} className="p-4 hover:bg-emerald-300/5 transition" data-testid={`ae-history-row-${m.id}`}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <StatusBadge status={m.status} />
                      {m.delivery_status && m.delivery_status !== "pending" && (
                        <DeliveryBadge status={m.delivery_status} />
                      )}
                      <span className="text-white font-semibold text-sm truncate">{m.subject}</span>
                    </div>
                    <p className="text-xs text-emerald-100 truncate">
                      Para: <span className="text-emerald-200/80">{(m.to || []).join(", ")}</span>
                      {m.cc?.length > 0 && <> · CC: {m.cc.join(", ")}</>}
                    </p>
                    <p className="text-[11px] text-emerald-200/50 mt-1">
                      {m.status === "scheduled"
                        ? <>Agendado para <span className="text-amber-300">{new Date(m.scheduled_for).toLocaleString("pt-PT")}</span> · por {m.created_by_name || "—"}</>
                        : <>{new Date(m.sent_at).toLocaleString("pt-PT")}{m.sent_by_name && <> · por {m.sent_by_name}</>}</>}
                      {m.attachments_meta?.length > 0 && <> · {m.attachments_meta.length} anexo(s)</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 md:shrink-0">
                    {m.status === "scheduled" ? (
                      <button onClick={() => cancelScheduled(m.id)}
                              className="rounded-lg border border-red-400/40 bg-red-400/10 px-3 py-1.5 text-xs text-red-200 hover:bg-red-400/15 inline-flex items-center gap-1.5"
                              data-testid={`ae-cancel-scheduled-${m.id}`}>
                        <X size={11} /> Cancelar
                      </button>
                    ) : (
                      <>
                        <button onClick={() => viewDetail(m.id)}
                                className="rounded-lg border border-emerald-300/30 bg-emerald-300/5 px-3 py-1.5 text-xs text-emerald-100 hover:bg-emerald-300/10 inline-flex items-center gap-1.5"
                                data-testid={`ae-history-view-${m.id}`}>
                          <Eye size={11} /> Ver
                        </button>
                        <button onClick={() => onJump(m)}
                                className="rounded-lg border border-amber-300/30 bg-amber-300/10 px-3 py-1.5 text-xs text-amber-200 hover:bg-amber-300/15 inline-flex items-center gap-1.5"
                                data-testid={`ae-history-edit-${m.id}`}>
                          <Copy size={11} /> Editar &amp; reenviar
                        </button>
                        <button onClick={() => resend(m.id)}
                                className="rounded-lg bg-amber-300 hover:bg-amber-200 text-emerald-950 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5"
                                data-testid={`ae-history-resend-${m.id}`}>
                          <Send size={11} /> Reenviar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {detail && <DetailModal m={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    sent: { label: "ENVIADO", cls: "bg-emerald-400/20 text-emerald-200", icon: CheckCircle2 },
    partial: { label: "PARCIAL", cls: "bg-amber-400/25 text-amber-100", icon: AlertTriangle },
    failed: { label: "FALHOU", cls: "bg-red-400/20 text-red-200", icon: AlertCircle },
    scheduled: { label: "AGENDADO", cls: "bg-blue-400/25 text-blue-100", icon: CalendarClock },
  }[status] || { label: status?.toUpperCase() || "—", cls: "bg-emerald-400/15 text-emerald-200", icon: CheckCircle2 };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold inline-flex items-center gap-1 ${cfg.cls}`}>
      <cfg.icon size={10} /> {cfg.label}
    </span>
  );
}

/* ───────────────────────────────────────────────────────────── TEMPLATES */

function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/admin/emails/templates");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => { load(); }, []);

  async function remove(id) {
    if (!window.confirm("Eliminar este modelo?")) return;
    try {
      await api.delete(`/admin/emails/templates/${id}`);
      toast.success("Modelo eliminado.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const grouped = groupBy(items, "category");

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-emerald-200/70">{items.length} modelos disponíveis</p>
        <button onClick={() => setCreating(true)}
                className="rounded-lg bg-amber-300 hover:bg-amber-200 text-emerald-950 px-4 py-2 text-sm font-bold inline-flex items-center gap-2"
                data-testid="ae-template-new">
          <Plus size={14} /> Novo Modelo
        </button>
      </div>
      {Object.entries(grouped).map(([cat, arr]) => (
        <div key={cat} className="rounded-2xl border border-emerald-300/15 bg-emerald-950/40 overflow-hidden">
          <div className="px-4 py-2 border-b border-emerald-300/15 bg-emerald-900/40">
            <h3 className="text-amber-300 font-semibold text-sm uppercase tracking-wider">{cat} ({arr.length})</h3>
          </div>
          <div className="divide-y divide-emerald-300/10">
            {arr.map((t) => (
              <div key={t.id} className="p-3 hover:bg-emerald-300/5 transition flex items-center justify-between gap-3"
                   data-testid={`ae-template-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white font-medium text-sm truncate">{t.name}</p>
                    {t.language && t.language !== "pt" && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-300/20 text-amber-200 uppercase tracking-wider font-bold">
                        {t.language}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-emerald-200/50 truncate">{t.subject}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setEditing(t)}
                          className="rounded-lg border border-emerald-300/30 bg-emerald-300/5 px-3 py-1.5 text-xs text-emerald-100 hover:bg-emerald-300/10 inline-flex items-center gap-1.5"
                          data-testid={`ae-template-edit-${t.id}`}>
                    <Pencil size={11} /> Editar
                  </button>
                  <button onClick={() => remove(t.id)}
                          className="rounded-lg border border-red-400/40 bg-red-400/5 px-3 py-1.5 text-xs text-red-200 hover:bg-red-400/15 inline-flex items-center gap-1.5"
                          data-testid={`ae-template-delete-${t.id}`}>
                    <Trash2 size={11} /> Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {(editing || creating) && (
        <TemplateEditor initial={editing}
                        onClose={() => { setEditing(null); setCreating(false); }}
                        onSaved={() => { setEditing(null); setCreating(false); load(); }} />
      )}
    </div>
  );
}

function TemplateEditor({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [category, setCategory] = useState(initial?.category || "Comercial");
  const [language, setLanguage] = useState(initial?.language || "pt");
  const [subject, setSubject] = useState(initial?.subject || "");
  const [body, setBody] = useState(initial?.body_html || "");
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !saving) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  async function save() {
    if (!name.trim() || !category.trim() || !subject.trim() || !body.trim()) {
      toast.error("Todos os campos são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      const payload = { name: name.trim(), category: category.trim(), subject: subject.trim(), body_html: body, language };
      if (initial?.id) {
        await api.patch(`/admin/emails/templates/${initial.id}`, payload);
        toast.success("Modelo atualizado.");
      } else {
        await api.post("/admin/emails/templates", payload);
        toast.success("Modelo criado.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  /** Use AI to translate this template's body to another language and save as a NEW template. */
  async function translateClone(targetLang) {
    if (!body.trim()) { toast.error("Sem conteúdo para traduzir."); return; }
    setTranslating(true);
    try {
      const [bodyRes, subjRes] = await Promise.all([
        api.post("/admin/emails/ai/translate", { text: body, target_language: targetLang, source_language: language }),
        api.post("/admin/emails/ai/translate", { text: `<p>${subject}</p>`, target_language: targetLang, source_language: language }),
      ]);
      const cleanSubject = (subjRes.data.body_html || "").replace(/<\/?[^>]+>/g, "").trim();
      const langSuffix = { en: "EN", es: "ES", fr: "FR", pt: "PT" }[targetLang];
      await api.post("/admin/emails/templates", {
        name: `${name} [${langSuffix}]`,
        category,
        subject: cleanSubject || subject,
        body_html: bodyRes.data.body_html,
        language: targetLang,
      });
      toast.success(`Modelo traduzido criado em ${langSuffix}.`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTranslating(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
         data-testid="ae-template-editor">
      <div className="bg-emerald-950 border border-emerald-300/20 rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-emerald-300/15">
          <h2 className="text-white font-bold text-lg">{initial ? "Editar Modelo" : "Novo Modelo"}</h2>
          <button onClick={onClose} className="text-emerald-200/60 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Nome *</Label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                     className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm px-3 py-2"
                     data-testid="ae-template-name" />
            </div>
            <div>
              <Label>Categoria *</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm px-3 py-2"
                      data-testid="ae-template-category">
                {["Financeiro", "Comercial", "Operacional", "Atendimento", "Marketing"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <Label icon={<Languages size={11} />}>Idioma</Label>
              <select value={language} onChange={(e) => setLanguage(e.target.value)}
                      className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm px-3 py-2"
                      data-testid="ae-template-language">
                <option value="pt">Português</option>
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Assunto *</Label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                   className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm px-3 py-2"
                   data-testid="ae-template-subject" />
          </div>
          <div>
            <Label>Corpo HTML *</Label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10}
                      className="w-full rounded-lg bg-emerald-900/50 border border-emerald-300/20 text-white text-sm px-3 py-2 font-mono"
                      data-testid="ae-template-body" />
          </div>
        </div>
        <div className="p-4 border-t border-emerald-300/15 flex flex-wrap justify-end gap-2">
          {initial?.id && (
            <div className="mr-auto flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-amber-300/80 font-semibold mr-1 inline-flex items-center gap-1">
                <Sparkles size={10} /> IA: clonar para
              </span>
              {[["en", "EN"], ["es", "ES"], ["fr", "FR"], ["pt", "PT"]].filter(([k]) => k !== language).map(([k, lbl]) => (
                <button key={k} onClick={() => translateClone(k)} disabled={translating || saving}
                        className="text-xs rounded-lg border border-amber-300/40 bg-amber-300/10 text-amber-200 hover:bg-amber-300/20 px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`ae-template-translate-${k}`}>
                  <Languages size={10} /> {lbl}
                </button>
              ))}
              {translating && <span className="text-xs text-amber-300 ml-1">a traduzir…</span>}
            </div>
          )}
          <button onClick={onClose} className="rounded-lg border border-emerald-300/30 bg-emerald-300/5 px-4 py-2 text-sm text-emerald-100">
            Cancelar
          </button>
          <button onClick={save} disabled={saving}
                  className="rounded-lg bg-amber-300 hover:bg-amber-200 text-emerald-950 px-4 py-2 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="ae-template-save">
            <Save size={14} /> {saving ? "A guardar…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── helpers */

function Label({ children, icon }) {
  return (
    <label className="text-[11px] uppercase tracking-wider text-emerald-200/60 font-semibold mb-1 flex items-center gap-1.5">
      {icon}{children}
    </label>
  );
}

function RecipientField({ label, tags, input, setInput, onAdd, onRemove, testid, required }) {
  function handleKey(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      onAdd(input);
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      onRemove(tags[tags.length - 1]);
    }
  }
  return (
    <div>
      <Label>{label}{required ? " *" : ""}</Label>
      <div className="flex flex-wrap gap-1.5 rounded-lg bg-emerald-900/50 border border-emerald-300/20 px-2 py-1.5 focus-within:border-amber-300/50">
        {tags.map((t) => (
          <span key={t} className="text-xs bg-amber-300/20 text-amber-100 rounded-full px-2.5 py-1 inline-flex items-center gap-1.5">
            {t}
            <button onClick={() => onRemove(t)} className="hover:text-white"><X size={10} /></button>
          </span>
        ))}
        <input type="email" value={input} onChange={(e) => setInput(e.target.value)}
               onKeyDown={handleKey}
               onBlur={() => input && onAdd(input)}
               placeholder={tags.length === 0 ? "email@example.com" : ""}
               className="flex-1 min-w-[160px] bg-transparent text-white text-sm px-1 py-0.5 focus:outline-none"
               data-testid={`${testid}-input`} />
      </div>
    </div>
  );
}

function PreviewModal({ subject, body, placeholders = {}, onClose }) {
  const { company, logo } = useCompany();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && onClose) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Apply __KEY__ → value substitution so the preview matches what gets sent
  function applyPh(text) {
    if (!text) return text;
    let out = text;
    for (const [k, v] of Object.entries(placeholders || {})) {
      if (v == null) continue;
      out = out.split(`__${k}__`).join(String(v));
    }
    return out;
  }
  const finalSubject = applyPh(subject);
  const finalBody = applyPh(body);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
         data-testid="ae-preview-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-gray-900 font-bold">Pré-visualização — {finalSubject || "(sem assunto)"}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto flex-1">
          <div className="bg-[#f5f5f0] p-6">
            <div className="bg-white rounded-2xl max-w-xl mx-auto overflow-hidden border border-[#ecece5] shadow-md">
              {/* Header */}
              <div style={{ padding: "36px 32px 8px", textAlign: "center", background: "#fff" }}>
                <img src={logo} alt={company.trade_name} width="150"
                     style={{ display: "inline-block", width: 150, height: "auto", maxWidth: 150 }} />
              </div>
              <div style={{ padding: "0 32px 22px", textAlign: "center", background: "#fff" }}>
                <p style={{ margin: 0, color: "#d4a82e", fontSize: 12, letterSpacing: 3, textTransform: "uppercase", fontWeight: 600, fontFamily: "'Georgia','Helvetica Neue',Arial,sans-serif", fontStyle: "italic" }}>
                  {company.slogan}.
                </p>
              </div>
              {/* Gold separator */}
              <div style={{ padding: "0 32px" }}>
                <div style={{ height: 1, background: "linear-gradient(90deg,transparent 0%,#d4a82e 50%,transparent 100%)" }} />
              </div>
              {/* Body */}
              <div style={{ padding: "28px 36px 12px", color: "#2a3a35", fontSize: 14, lineHeight: 1.7 }}
                   dangerouslySetInnerHTML={{ __html: finalBody || "<p><em>Sem conteúdo…</em></p>" }} />
              {/* Signature */}
              <div style={{ padding: "8px 36px 22px", fontSize: 13, lineHeight: 1.6, color: "#2a3a35" }}>
                <p style={{ margin: "18px 0 4px 0", color: "#1a3a32", fontWeight: 500 }}>Com os melhores cumprimentos,</p>
                <p style={{ margin: 0, fontWeight: 700, color: "#1a3a32", fontSize: 15, letterSpacing: 0.3 }}>{company.trade_name}</p>
                <p style={{ margin: "4px 0 14px 0", color: "#7a8a86", fontSize: 12, letterSpacing: 0.2 }}>
                  Limpezas Profissionais · Lavandaria
                </p>
                <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>
                  <div><span style={{ color: "#7a8a86" }}>Telefone</span>&nbsp; <strong style={{ color: "#1a3a32" }}>{company.phone}</strong></div>
                  <div><span style={{ color: "#7a8a86" }}>WhatsApp</span>&nbsp; <strong style={{ color: "#1a3a32" }}>{company.whatsapp}</strong></div>
                  <div><span style={{ color: "#7a8a86" }}>E-mail</span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <strong style={{ color: "#1a3a32" }}>{company.email}</strong></div>
                  <div><span style={{ color: "#7a8a86" }}>Website</span>&nbsp;&nbsp; <strong style={{ color: "#1a3a32" }}>{company.website}</strong></div>
                </div>
              </div>
              {/* No-reply notice */}
              <div style={{ padding: "0 36px 22px" }}>
                <div style={{ borderLeft: "3px solid #d4a82e", background: "#fbf6e8", borderRadius: 6, padding: "14px 18px" }}>
                  <p style={{ margin: "0 0 6px 0", fontWeight: 700, color: "#8a6500", letterSpacing: 0.5, textTransform: "uppercase", fontSize: 10.5 }}>
                    Aviso importante
                  </p>
                  <p style={{ margin: "0 0 6px 0", fontSize: 12, lineHeight: 1.55, color: "#3a3a35" }}>
                    Este endereço de e-mail é utilizado apenas para envio de notificações e <strong>não recebe respostas</strong>.
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: "#3a3a35" }}>
                    Para nos contactar, escreva para <strong style={{ color: "#1a3a32" }}>{company.email}</strong>.
                  </p>
                </div>
              </div>
              {/* Footer */}
              <div style={{ background: "#fafaf5", padding: "16px 32px", textAlign: "center", fontSize: 10.5, color: "#9a9a90", borderTop: "1px solid #ecece5", letterSpacing: 0.3 }}>
                © {new Date().getFullYear()} {company.trade_name} · {company.slogan}.
                <br />{company.legal_name}{company.nipc ? ` · NIPC ${fmtNipc(company.nipc)}` : ""}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailModal({ m, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
         data-testid="ae-detail-modal">
      <div className="bg-emerald-950 border border-emerald-300/20 rounded-2xl w-full max-w-3xl my-8 shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-emerald-300/15">
          <div>
            <h2 className="text-white font-bold">{m.subject}</h2>
            <p className="text-xs text-emerald-200/60 mt-1">
              {new Date(m.sent_at).toLocaleString("pt-PT")} · <StatusBadge status={m.status} />
            </p>
          </div>
          <button onClick={onClose} className="text-emerald-200/60 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="text-xs text-emerald-100 space-y-1">
            <p><span className="text-emerald-200/60">Para:</span> {m.to.join(", ")}</p>
            {m.cc?.length > 0 && <p><span className="text-emerald-200/60">CC:</span> {m.cc.join(", ")}</p>}
            {m.bcc?.length > 0 && <p><span className="text-emerald-200/60">BCC:</span> {m.bcc.join(", ")}</p>}
            {m.attachments_meta?.length > 0 && (
              <p><span className="text-emerald-200/60">Anexos:</span> {m.attachments_meta.map((a) => a.filename).join(", ")}</p>
            )}
          </div>
          <div className="rounded-xl border border-emerald-300/15 bg-white text-gray-900 p-4 max-h-[50vh] overflow-y-auto"
               dangerouslySetInnerHTML={{ __html: m.body_html }} />
        </div>
      </div>
    </div>
  );
}

function AiPanel({ busy, onGenerate, onImprove, onGrammar, onTranslate, onAdaptTone }) {
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState("profissional");
  return (
    <div className="rounded-xl border border-amber-300/30 bg-amber-300/5 p-3 mb-2 space-y-3" data-testid="ae-ai-panel">
      {/* Generate from prompt */}
      <div>
        <p className="text-[11px] uppercase tracking-wider text-amber-300/80 font-semibold mb-1.5 inline-flex items-center gap-1.5">
          <Sparkles size={11} /> Gerar e-mail a partir de descrição
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input type="text" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                 placeholder="Ex: pedido de pagamento da limpeza de Maio, 75€"
                 className="flex-1 rounded-lg bg-emerald-900/60 border border-emerald-300/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-amber-300/50"
                 data-testid="ae-ai-prompt" />
          <select value={tone} onChange={(e) => setTone(e.target.value)}
                  className="rounded-lg bg-emerald-900/60 border border-emerald-300/20 text-white text-sm px-3 py-2"
                  data-testid="ae-ai-tone">
            <option value="profissional">Profissional</option>
            <option value="formal">Formal</option>
            <option value="casual">Casual</option>
            <option value="persuasivo">Persuasivo</option>
            <option value="empático">Empático</option>
          </select>
          <button onClick={() => onGenerate(prompt, tone)} disabled={busy}
                  type="button"
                  className="rounded-lg bg-amber-300 hover:bg-amber-200 text-emerald-950 px-4 py-2 text-xs font-bold inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="ae-ai-generate-btn">
            <Wand2 size={12} /> Gerar
          </button>
        </div>
      </div>
      {/* Actions on existing body */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-amber-300/20">
        <AiActionBtn icon={Sparkles} onClick={onImprove} busy={busy} testid="ae-ai-improve">Melhorar texto</AiActionBtn>
        <AiActionBtn icon={ClipboardCheck} onClick={onGrammar} busy={busy} testid="ae-ai-grammar">Corrigir gramática</AiActionBtn>
        <ToneDropdown onPick={onAdaptTone} busy={busy} />
        <LangDropdown onPick={onTranslate} busy={busy} />
      </div>
    </div>
  );
}

function AiActionBtn({ icon: Icon, onClick, busy, children, testid }) {
  return (
    <button onClick={onClick} disabled={busy} type="button"
            className="text-xs rounded-lg border border-emerald-300/30 bg-emerald-900/50 text-emerald-100 hover:bg-emerald-900/70 px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid={testid}>
      <Icon size={11} /> {children}
    </button>
  );
}

function ToneDropdown({ onPick, busy }) {
  const [open, setOpen] = useState(false);
  const tones = ["profissional", "formal", "casual", "persuasivo", "empático"];
  return (
    <div className="relative inline-block">
      <button onClick={() => setOpen(!open)} disabled={busy} type="button"
              className="text-xs rounded-lg border border-emerald-300/30 bg-emerald-900/50 text-emerald-100 hover:bg-emerald-900/70 px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="ae-ai-tone-toggle">
        <MessageSquare size={11} /> Adaptar tom
      </button>
      {open && (
        <div className="absolute z-10 mt-1 rounded-lg border border-emerald-300/30 bg-emerald-950 shadow-xl min-w-[140px]"
             data-testid="ae-ai-tone-menu">
          {tones.map((t) => (
            <button key={t} onClick={() => { onPick(t); setOpen(false); }} type="button"
                    className="block w-full text-left text-xs px-3 py-1.5 text-emerald-100 hover:bg-amber-300/10 first:rounded-t-lg last:rounded-b-lg capitalize"
                    data-testid={`ae-ai-tone-${t}`}>
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LangDropdown({ onPick, busy }) {
  const [open, setOpen] = useState(false);
  const langs = { en: "English", es: "Español", fr: "Français", pt: "Português" };
  return (
    <div className="relative inline-block">
      <button onClick={() => setOpen(!open)} disabled={busy} type="button"
              className="text-xs rounded-lg border border-emerald-300/30 bg-emerald-900/50 text-emerald-100 hover:bg-emerald-900/70 px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="ae-ai-translate-toggle">
        <Languages size={11} /> Traduzir
      </button>
      {open && (
        <div className="absolute z-10 mt-1 rounded-lg border border-emerald-300/30 bg-emerald-950 shadow-xl min-w-[140px]"
             data-testid="ae-ai-lang-menu">
          {Object.entries(langs).map(([k, v]) => (
            <button key={k} onClick={() => { onPick(k); setOpen(false); }} type="button"
                    className="block w-full text-left text-xs px-3 py-1.5 text-emerald-100 hover:bg-amber-300/10 first:rounded-t-lg last:rounded-b-lg"
                    data-testid={`ae-ai-lang-${k}`}>
              {v}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DeliveryBadge({ status }) {
  const cfg = {
    delivered: { label: "ENTREGUE", cls: "bg-emerald-500/30 text-emerald-100", icon: MailCheck },
    opened: { label: "ABERTO", cls: "bg-amber-400/30 text-amber-100", icon: MailOpen },
    bounced: { label: "DEVOLVIDO", cls: "bg-red-500/30 text-red-100", icon: MailX },
    complained: { label: "SPAM", cls: "bg-red-500/30 text-red-100", icon: MailX },
    delayed: { label: "ATRASADO", cls: "bg-amber-400/20 text-amber-200", icon: Clock },
    sent_confirmed: { label: "CONFIRMADO", cls: "bg-emerald-400/15 text-emerald-200", icon: CheckCircle2 },
  }[status];
  if (!cfg) return null;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-bold inline-flex items-center gap-1 ${cfg.cls}`}
          data-testid={`ae-delivery-${status}`}>
      <cfg.icon size={10} /> {cfg.label}
    </span>
  );
}

function groupBy(arr, key) {
  return arr.reduce((acc, x) => {
    const k = x[key] || "—";
    if (!acc[k]) acc[k] = [];
    acc[k].push(x);
    return acc;
  }, {});
}

function fmtBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
